/**
 * OWA-1339: normalize the stored value of an LZ4-compressed entity field (Conflict.report) on its way into an entity.
 *
 * The entity keeps a compressed field as a Uint8Array and decompresses it in its getter. Between 2021 and 2024 some
 * saves serialized that Uint8Array with JSON.stringify, which writes a byte map (`{"0":4,"1":34}` and so on) instead of the
 * text, so the field shows as JSON. A byte map can also reach the constructor as a plain object with numeric keys; the
 * getter cannot decompress that and throws, which breaks the card, every save (toJSON) and search.
 *
 * The stored byte maps hold LZ4 frames (magic 04 22 4D 18), written by an earlier LZ4 library. Today's lz4-wasm uses
 * a size-prefixed block format and cannot read frames, so frames are decoded here and returned as text. A byte map in
 * today's format is returned as bytes for the getter to decompress. Anything else that is not a string or a Uint8Array
 * becomes a string, so the getter never throws.
 */
import * as lz4 from 'lz4-wasm';

const LZ4_FRAME_MAGIC = [0x04, 0x22, 0x4d, 0x18];

/**
 * The bytes of a byte map (keys "0".."n-1", values 0..255), or null when the value is not one.
 *
 * @param {unknown} value - a JSON string or a parsed object
 * @returns {Uint8Array|null}
 */
export function byteMapToBytes(value: unknown): Uint8Array | null {
    let map: unknown = value;
    if (typeof value === 'string') {
        if (!value.startsWith('{"0":')) return null;
        try {
            map = JSON.parse(value);
        }
        catch {
            return null;
        }
    }
    if (!map || typeof map !== 'object' || Array.isArray(map) || map instanceof Uint8Array) return null;

    const entries = Object.entries(map as Record<string, unknown>);
    if (entries.length === 0) return null;
    const bytes = new Uint8Array(entries.length);
    for (const [key, byte] of entries) {
        const index = Number(key);
        if (!Number.isInteger(index) || index < 0 || index >= entries.length) return null;
        if (!Number.isInteger(byte) || (byte as number) < 0 || (byte as number) > 255) return null;
        bytes[index] = byte as number;
    }
    return bytes;
}

/**
 * Decode one LZ4 block (sequences of literals and back-references) onto the end of `out`.
 *
 * @param {Uint8Array} src
 * @param {number[]} out
 */
function decodeLz4Block(src: Uint8Array, out: number[]): void {
    let i = 0;
    while (i < src.length) {
        const token = src[i++];
        let literals = token >> 4;
        if (literals === 15) {
            let b: number;
            do { b = src[i++]; literals += b; } while (b === 255 && i < src.length);
        }
        if (i + literals > src.length) throw new Error('LZ4 literals run past the block');
        for (let k = 0; k < literals; k++) out.push(src[i++]);
        if (i >= src.length) break;

        const offset = src[i] | (src[i + 1] << 8);
        i += 2;
        let matchLength = token & 15;
        if (matchLength === 15) {
            let b: number;
            do { b = src[i++]; matchLength += b; } while (b === 255 && i < src.length);
        }
        matchLength += 4;
        const start = out.length - offset;
        if (offset === 0 || start < 0) throw new Error('LZ4 match offset out of range');
        for (let k = 0; k < matchLength; k++) out.push(out[start + k]);
    }
}

/**
 * Decode an LZ4 frame to UTF-8 text.  Throws on a malformed frame or invalid UTF-8.
 *
 * @param {Uint8Array} bytes - starting with the frame magic number
 * @returns {string}
 */
export function decodeLz4Frame(bytes: Uint8Array): string {
    if (!LZ4_FRAME_MAGIC.every((b, i) => bytes[i] === b)) throw new Error('Not an LZ4 frame');
    const flags = bytes[4];
    // The stored frames have FLG 0x40: linked (dependent) blocks. Matches resolve against all output decoded so far,
    // which covers both linked and independent blocks.
    // Header: magic (4), FLG, BD, optional content size (8) and dictionary ID (4), header checksum (1).
    // Header and block checksums are not verified; strict UTF-8 decoding of the result catches corrupt output.
    let i = 6 + ((flags & 0x08) ? 8 : 0) + ((flags & 0x01) ? 4 : 0) + 1;
    const blockChecksums = (flags & 0x10) !== 0;
    const out: number[] = [];
    for (;;) {
        if (i + 4 > bytes.length) throw new Error('LZ4 frame ends without an end mark');
        const size = (bytes[i] | (bytes[i + 1] << 8) | (bytes[i + 2] << 16) | (bytes[i + 3] << 24)) >>> 0;
        i += 4;
        if (size === 0) break;
        const length = size & 0x7fffffff;
        if (i + length > bytes.length) throw new Error('LZ4 block runs past the frame');
        const block = bytes.subarray(i, i + length);
        if (size & 0x80000000) block.forEach(b => out.push(b));
        else decodeLz4Block(block, out);
        i += length + (blockChecksums ? 4 : 0);
    }
    return new TextDecoder('utf-8', {fatal: true}).decode(Uint8Array.from(out));
}

/**
 * Normalize a compressed field's incoming value.
 *
 * @param {unknown} value - the raw value from the server, a cache, or another entity
 * @returns {string|Uint8Array|null} text, bytes the getter can decompress, or null
 */
export function normalizeCompressedField(value: unknown): string | Uint8Array | null {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Uint8Array) return value;

    const bytes = byteMapToBytes(value);
    if (bytes) {
        if (LZ4_FRAME_MAGIC.every((b, i) => bytes[i] === b)) {
            try {
                return decodeLz4Frame(bytes);
            }
            catch (err) {
                console.error('A byte-map field holds an LZ4 frame that does not decode', err);
            }
        }
        else {
            try {
                lz4.decompress(bytes);
                return bytes;
            }
            catch (err) {
                console.error('A byte-map field does not decompress', err);
            }
        }
    }

    if (typeof value === 'string') return value;
    // A plain object we cannot decode: keep it as text so it is saved back unchanged and the getter does not throw
    return JSON.stringify(value);
}
